import { useState } from "react";
import { Sparkles, Copy, RefreshCw, FileText, TrendingUp, PieChart, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const writingTypes = [
  { value: "general", label: "General Content", icon: FileText, description: "General grant proposal content" },
  { value: "executive_summary", label: "Executive Summary", icon: Lightbulb, description: "Business overview for investors" },
  { value: "market_analysis", label: "Market Analysis", icon: TrendingUp, description: "Daytona Beach market opportunity" },
  { value: "financial_projection", label: "Financial Projection", icon: PieChart, description: "Revenue and ROI projections" },
];

const promptSuggestions = [
  "Write a compelling introduction for our SBA grant application",
  "Describe the market opportunity for massage therapy in Daytona Beach",
  "Explain why preventative care is important and growing",
  "Write about Lacresha Ramos's qualifications and experience",
  "Describe the competitive advantages of Millennium Wellness Spa",
  "Write about the health benefits of regular massage therapy",
  "Create a use of funds section for a $50,000 grant",
  "Write about our growth strategy and expansion plans"
];

export const AIWriterPage = () => {
  const [prompt, setPrompt] = useState("");
  const [context, setContext] = useState("");
  const [writingType, setWritingType] = useState("general");
  const [generatedText, setGeneratedText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    setIsGenerating(true);
    setGeneratedText("");

    try {
      const response = await axios.post(`${API}/ai/generate`, {
        prompt,
        context,
        writing_type: writingType
      });
      
      setGeneratedText(response.data.generated_text);
      toast.success("Content generated successfully");
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Failed to generate content";
      toast.error(errorMessage);
      console.error("AI generation error:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedText);
    toast.success("Copied to clipboard");
  };

  const handleSuggestionClick = (suggestion) => {
    setPrompt(suggestion);
  };

  const selectedType = writingTypes.find(t => t.value === writingType);

  return (
    <div data-testid="ai-writer-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#0F172A] font-display flex items-center gap-3">
          <Sparkles className="text-[#D4AF37]" />
          AI Writing Assistant
        </h1>
        <p className="text-slate-500 mt-1">
          Generate professional grant proposal content powered by AI
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Input Panel */}
        <div className="space-y-6">
          {/* Writing Type Selection */}
          <div className="bg-white border border-slate-200 p-6">
            <label className="block text-sm font-medium text-[#0F172A] mb-3">
              Content Type
            </label>
            <div className="grid grid-cols-2 gap-3">
              {writingTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    onClick={() => setWritingType(type.value)}
                    className={`p-4 border text-left transition-all ${
                      writingType === type.value 
                        ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid={`type-${type.value}`}
                  >
                    <Icon size={20} className={writingType === type.value ? 'text-[#D4AF37]' : 'text-slate-400'} />
                    <p className="font-medium text-[#0F172A] mt-2 text-sm">{type.label}</p>
                    <p className="text-xs text-slate-500 mt-1">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Prompt Input */}
          <div className="bg-white border border-slate-200 p-6">
            <label className="block text-sm font-medium text-[#0F172A] mb-3">
              What would you like to write?
            </label>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what content you need for your grant application..."
              className="min-h-[120px] mb-4"
              data-testid="ai-prompt-input"
            />
            
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              Additional Context (optional)
            </label>
            <Textarea
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Any specific details or requirements..."
              className="min-h-[80px] mb-4"
              data-testid="ai-context-input"
            />

            <Button 
              onClick={handleGenerate}
              disabled={isGenerating || !prompt.trim()}
              className="btn-secondary w-full"
              data-testid="ai-generate-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw size={18} className="mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles size={18} className="mr-2" />
                  Generate Content
                </>
              )}
            </Button>
          </div>

          {/* Suggestions */}
          <div className="bg-slate-50 border border-slate-200 p-6">
            <p className="text-sm font-medium text-[#0F172A] mb-3">Prompt Suggestions</p>
            <div className="flex flex-wrap gap-2">
              {promptSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="text-xs px-3 py-1.5 bg-white border border-slate-200 text-slate-600 hover:border-[#D4AF37] hover:text-[#0F172A] transition-colors"
                  data-testid={`suggestion-${index}`}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Output Panel */}
        <div className="bg-white border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-[#0F172A]">Generated Content</h3>
              {selectedType && (
                <p className="text-sm text-slate-500">Type: {selectedType.label}</p>
              )}
            </div>
            {generatedText && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleCopy}
                data-testid="ai-copy-btn"
              >
                <Copy size={16} className="mr-2" />
                Copy
              </Button>
            )}
          </div>
          
          <div 
            className="min-h-[400px] p-4 bg-slate-50 border border-slate-100 overflow-y-auto"
            data-testid="ai-output"
          >
            {isGenerating ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="spinner mx-auto mb-4" />
                  <p className="text-slate-500">Generating professional content...</p>
                </div>
              </div>
            ) : generatedText ? (
              <div className="prose prose-slate max-w-none whitespace-pre-wrap">
                {generatedText}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-center">
                <div>
                  <Sparkles size={48} className="text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">
                    Select a content type, enter your prompt, and click Generate
                  </p>
                  <p className="text-sm text-slate-400 mt-2">
                    AI will create professional grant proposal content tailored for Millennium Wellness Spa
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIWriterPage;
